import React, { useCallback, useEffect, useRef, useState } from 'react';

import '../../assets/styles/HeroBanner.css';
import AppLink from '../common/AppLink';
import { useLanguage } from '../../context/LanguageContext';
import { useContentList } from '../../hooks/useContentList';
import { getSiteMessages } from '../../locales/site';
import { getTranslatedField } from '../../utils/cms';

const SLIDE_DURATION_MS = 680;
const AUTOPLAY_DELAY_MS = 5500;
const idleSlide = (index = 0) => ({ index, target: null, direction: 'next', ready: false });
const normalizeBannerText = (value = '') => String(value || '').trim();

function HeroBanner() {
  const { currentLanguageCode } = useLanguage();
  const texts = getSiteMessages(currentLanguageCode).home.heroBanner;
  const { items } = useContentList('banners', { revalidate: true });
  const slides = React.useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        desktopImage: item.image,
        mobileImage: item.mobileImage || item.image,
        title: normalizeBannerText(getTranslatedField(item, 'title', item.title, currentLanguageCode)),
        text: normalizeBannerText(getTranslatedField(item, 'subtitle', '', currentLanguageCode)),
        primaryButtonText: normalizeBannerText(getTranslatedField(item, 'primaryButtonText', '', currentLanguageCode)),
        primaryButtonLink: normalizeBannerText(item.primaryButtonLink)
      })),
    [items, currentLanguageCode]
  );

  const [slider, setSlider] = useState(idleSlide);
  const [userPaused, setUserPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const stageRef = useRef(null);
  const swipeRef = useRef(null);
  const ignoreClickUntil = useRef(0);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(media.matches);
    const updateVisibility = () => setPageVisible(!document.hidden);
    media.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      media.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => setSlider(idleSlide()), [slides.length]);

  const changeSlide = useCallback(
    (destination, relative = false) => {
      if (slides.length < 2) return;
      setSlider((previous) => {
        if (previous.target !== null) return previous;
        const target =
          (relative ? previous.index + destination + slides.length : destination) % slides.length;
        if (target === previous.index) return previous;
        if (reducedMotion) return idleSlide(target);
        const forward = relative ? destination > 0 : target > previous.index;
        return {
          index: previous.index,
          target,
          direction: forward ? 'next' : 'previous',
          ready: false
        };
      });
    },
    [slides.length, reducedMotion]
  );

  const finishTransition = useCallback(() => {
    setSlider((previous) => (previous.target === null ? previous : idleSlide(previous.target)));
  }, []);

  const markIncomingReady = useCallback(() => {
    setSlider((previous) =>
      previous.target === null || previous.ready ? previous : { ...previous, ready: true }
    );
  }, []);

  // Keep the current banner visible until the next image is ready. This also
  // handles cached images whose load event completed before the effect ran.
  useEffect(() => {
    if (slider.target === null || slider.ready) return;
    const image = stageRef.current?.querySelector('.hero-slide--incoming img');
    if (!image || image.complete) {
      markIncomingReady();
      return;
    }
    const timer = window.setTimeout(markIncomingReady, 4000);
    return () => window.clearTimeout(timer);
  }, [slider.target, slider.ready, markIncomingReady]);

  useEffect(() => {
    if (slider.target === null || !slider.ready) return;
    if (reducedMotion) {
      finishTransition();
      return;
    }
    // Recover if an animation is interrupted by a resize or tab change.
    const timer = window.setTimeout(finishTransition, SLIDE_DURATION_MS + 100);
    return () => window.clearTimeout(timer);
  }, [slider.target, slider.ready, reducedMotion, finishTransition]);

  useEffect(() => {
    if (
      slides.length < 2 ||
      slider.target !== null ||
      userPaused ||
      hovered ||
      focused ||
      dragging ||
      !pageVisible
    )
      return;
    const timer = window.setTimeout(() => changeSlide(1, true), AUTOPLAY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    slides.length,
    slider.index,
    slider.target,
    userPaused,
    hovered,
    focused,
    dragging,
    pageVisible,
    changeSlide
  ]);

  // Warm only the next image; do not download every full-size banner at startup.
  useEffect(() => {
    if (slides.length < 2 || !pageVisible) return;
    const next = slides[(slider.index + 1) % slides.length];
    const source = window.matchMedia('(max-width: 760px)').matches
      ? next.mobileImage
      : next.desktopImage;
    if (!source) return;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.src = source;
  }, [slides, slider.index, pageVisible]);

  const cancelSwipe = () => {
    swipeRef.current = null;
    setDragging(false);
  };

  const handlePointerDown = (event) => {
    if (
      event.pointerType === 'mouse' ||
      !event.isPrimary ||
      slides.length < 2 ||
      event.target.closest('button')
    )
      return;
    swipeRef.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    setDragging(true);
  };

  const handlePointerUp = (event) => {
    const start = swipeRef.current;
    cancelSwipe();
    if (!start || start.id !== event.pointerId) return;
    const distanceX = event.clientX - start.x;
    const distanceY = event.clientY - start.y;
    const threshold = Math.max(32, Math.min(72, event.currentTarget.clientWidth * 0.12));
    if (Math.abs(distanceX) < threshold || Math.abs(distanceX) <= Math.abs(distanceY) * 1.2) return;
    ignoreClickUntil.current = Date.now() + 500;
    changeSlide(distanceX < 0 ? 1 : -1, true);
  };

  if (!slides.length) return null;
  const activeIndex = Math.min(slider.index, slides.length - 1);
  const visibleIndices = slider.target === null ? [activeIndex] : [activeIndex, slider.target];

  return (
    <section
      className="hero-slider"
      aria-roledescription="carousel"
      aria-label={texts.ariaLabel}
      tabIndex={0}
      style={{ '--hero-slide-duration': `${SLIDE_DURATION_MS}ms` }}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
      onFocusCapture={(event) => {
        // Faqat klaviatura fokusi autoplayni to'xtatadi. Telefonda bannerga yoki
        // nuqtaga tegish ham fokus beradi - avval shu sabab autoplay butunlay to'xtab qolardi.
        if (event.target.matches?.(':focus-visible')) setFocused(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={cancelSwipe}
      onClickCapture={(event) => {
        if (Date.now() < ignoreClickUntil.current) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        changeSlide(event.key === 'ArrowRight' ? 1 : -1, true);
      }}
    >
      <div className="hero-slides" ref={stageRef}>
        {visibleIndices.map((index) => {
          const slide = slides[index];
          if (!slide) return null;
          const incoming = slider.target === index;
          const moving = slider.target !== null && slider.ready;
          const hasContent = Boolean(slide.title || slide.text || slide.primaryButtonText);
          return (
            <article
              key={slide.id || index}
              className={`hero-slide ${hasContent ? 'hero-slide--with-content' : 'hero-slide--media-only'} ${incoming ? 'hero-slide--incoming' : 'hero-slide--current'} ${moving ? `hero-slide--${incoming ? 'enter' : 'exit'}-${slider.direction}` : ''}`}
              role="group"
              aria-roledescription={texts.slideLabel}
              aria-label={`${index + 1} / ${slides.length}`}
              aria-hidden={incoming}
              inert={slider.target !== null ? true : undefined}
              onAnimationEnd={(event) => {
                if (!incoming && event.target === event.currentTarget) finishTransition();
              }}
            >
              <div className="hero-media">
                {slide.desktopImage && (
                  <picture>
                    <source media="(max-width: 760px)" srcSet={slide.mobileImage} />
                    <img
                      className="hero-slide-image"
                      src={slide.desktopImage}
                      alt={hasContent ? '' : slide.title || `${texts.slideLabel} ${index + 1}`}
                      fetchPriority={index === 0 && !incoming ? 'high' : 'auto'}
                      loading="eager"
                      decoding="async"
                      draggable={false}
                      onLoad={incoming ? markIncomingReady : undefined}
                      onError={incoming ? markIncomingReady : undefined}
                    />
                  </picture>
                )}
              </div>
              {hasContent && (
                <>
                  <div className="hero-gradient" />
                  <div className="hero-content-shell">
                    <div className="hero-content">
                      {slide.title && <h1>{slide.title}</h1>}
                      {slide.text && <p>{slide.text}</p>}
                      {slide.primaryButtonText && (
                        <div className="hero-buttons">
                          <AppLink
                            href={slide.primaryButtonLink || '#'}
                            className="hero-btn primary"
                          >
                            {slide.primaryButtonText}
                            <span aria-hidden="true">→</span>
                          </AppLink>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </article>
          );
        })}
      </div>

      {slides.length > 1 && (
        <div className="hero-controls" aria-busy={slider.target !== null}>
          <button
            type="button"
            className="slider-arrow left"
            onClick={() => changeSlide(-1, true)}
            aria-label={texts.previousSlide}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m14 6-6 6 6 6" />
            </svg>
          </button>
          <div className="slider-dots">
            {slides.map((slide, index) => (
              <button
                key={slide.id || index}
                type="button"
                className={activeIndex === index ? 'active' : ''}
                onClick={() => changeSlide(index)}
                aria-label={`${texts.slideLabel} ${index + 1}`}
                aria-current={activeIndex === index ? 'true' : undefined}
              >
                <span />
              </button>
            ))}
          </div>
          <button
            type="button"
            className="slider-arrow right"
            onClick={() => changeSlide(1, true)}
            aria-label={texts.nextSlide}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m10 6 6 6-6 6" />
            </svg>
          </button>
          <button
            type="button"
            className="hero-autoplay"
            onClick={() => {
              if (userPaused) {
                setFocused(false);
                setHovered(false);
              }
              setUserPaused((previous) => !previous);
            }}
            aria-label={userPaused ? texts.resumeSlides : texts.pauseSlides}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              {userPaused ? <path d="m9 5 10 7-10 7z" /> : <path d="M9 5v14M15 5v14" />}
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}

export default HeroBanner;
